import React, { useState, forwardRef, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Divider,
    FormControlLabel,
    Checkbox,
    TextField,
    Typography,
    Box,
    Slide,
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import { CandidateFiters } from '../CandidateManagement/CandidateContent/CandidateContent';
import { theme } from '../../../theme';
import { PreboardingCandidatesFiltersType } from './AllCandidates';

// Slide transition component
const Transition = forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement<any, any>;
    },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="left" ref={ref} {...props} />;
});

interface FilterSortDialogProps {
    open: boolean;
    onClose: () => void;
    onApply: (filters: any) => void;
    statusOptions?: { value: string, label: string }[];
    filters: PreboardingCandidatesFiltersType;
    handleClearFilterAPI: () => void;
    filterFor?: string
}

const FilterPreboardingCandidates: React.FC<FilterSortDialogProps> = ({ open, onClose, onApply, statusOptions = [], filters, handleClearFilterAPI, filterFor }) => {
    const [recruiterName, setRecruiterName] = useState<string>('');
    const [department, setDepartment] = useState<string>('');
    const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]); // Track selected statuses
    const [pendingWithFilter, setPendingWithFilter] = useState<string | null>(null); // Track "Unmatched" and "Matched" selectio
    const [jobTitle, setJobTitle] = useState('')
    const [location, setLocation] = useState('')
    useEffect(() => {
        if (open) {
            setRecruiterName(filters.recruiterName);
            setDepartment(filters.department);
            setSelectedStatuses(filters.filterByStatus ?? []); // Default to empty array
            setPendingWithFilter(filters.pendingWithFilter ?? null); // Default to null
            setJobTitle(filters.jobTitle || "")
            setLocation(filters.location || "")
        }
    }, [open, filters]);

    const handleApplyFilters = () => {
        onApply({
            recruiterName,
            department,
            filterByStatus: selectedStatuses,
            pendingWithFilter,
            jobTitle,
            location
        });
        onClose();
    };

    const handleClearFilters = () => {
        const hasFiltersApplied =
            recruiterName ||
            department ||
            selectedStatuses.length > 0 ||
            pendingWithFilter ||
            jobTitle ||
            location

        setRecruiterName('');
        setDepartment('');
        setSelectedStatuses([]);
        setPendingWithFilter(null);
        setJobTitle('')
        setLocation('')

        if (hasFiltersApplied) {
            handleClearFilterAPI()
        }

        onClose();

    };

    const handleStatusChange = (value: string) => {
        setSelectedStatuses((prev) =>
            prev.includes(value) ? prev.filter((status) => status !== value) : [...prev, value]
        );
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            TransitionComponent={Transition}
            fullScreen
            BackdropProps={{ style: { backgroundColor: "rgba(0,0,0,0.5)",}}}
            PaperProps={{
                sx: {
                    width: '22vw',
                    maxWidth: '350px',
                    height: '100vh',
                    margin: 0,
                    overflow: 'hidden',
                    position: 'absolute',
                    right: 0,
                },
            }}
        >
            <DialogTitle sx={{ display: 'flex', p: '20px 15px 0px 10px' }}>
                <Box
                    color={theme.palette.secondary.main}
                    sx={{
                        border: '1px solid #EEEEEE',
                        bgcolor: theme.palette.secondary.light,
                        // margin: '6px',
                        mr: '1.6vw',
                        width: '3.4vw',
                        height: '3.4vw',
                        borderRadius: '1vw',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <i className="fa-solid fa-sliders"></i>
                </Box>
                <Box>
                    <Typography variant="h6">Filter & Sort</Typography>
                    <Typography variant="caption">Filters to get the best results</Typography>
                </Box>
            </DialogTitle>

            <Divider sx={{ mb: 1.2, mt: 2 }} />

            <DialogContent sx={{ overflowY: 'auto', pt: 0 }}>
                <DialogActions sx={{ justifyContent: 'space-between', p: 0, height: '24px' }}>
                    <Typography variant="subtitle1" whiteSpace={"nowrap"}>Add Filter</Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                        <Button onClick={handleClearFilters} color="secondary" sx={{ ml: 'auto', position: 'relative', right: -10 }}>
                            Clear
                        </Button>
                    </Box>
                </DialogActions>

                {/* Recruiter Input */}
                <TextField
                    fullWidth
                    size="small"
                    label="Recruiter"
                    margin="dense"
                    value={recruiterName}
                    onChange={(e) => setRecruiterName(e.target.value)}
                    autoComplete='off'
                />

                <TextField
                    fullWidth
                    size="small"
                    label="Department"
                    margin="dense"
                    value={department}
                    onChange={(e) => setDepartment(e.target.value)}
                    autoComplete='off'
                />

                {
                    filterFor ? (
                        <>
                            <TextField
                                fullWidth
                                size="small"
                                label="Job Title"
                                margin="dense"
                                value={jobTitle}
                                onChange={(e) => setJobTitle(e.target.value)}
                                autoComplete='off'
                            />
                            <TextField
                                fullWidth
                                size="small"
                                label="Location"
                                margin="dense"
                                value={location}
                                onChange={(e) => setLocation(e.target.value)}
                                autoComplete='off'
                            />
                        </>

                    ) : (
                        <></>
                    )
                }

                {/* Match Filter */}

                {
                    filterFor ? (
                        <></>
                    ) : (
                        <>
                            <Typography variant="subtitle1" sx={{ mt: 1.5 }}>
                                Pending With
                            </Typography>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={pendingWithFilter === 'Recruiter'}
                                        onChange={() => setPendingWithFilter(pendingWithFilter === 'Recruiter' ? null : 'Recruiter')}
                                    />
                                }
                                label="Recruiter"
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={pendingWithFilter === 'Candidate'}
                                        onChange={() => setPendingWithFilter(pendingWithFilter === 'Candidate' ? null : 'Candidate')}
                                    />
                                }
                                label="Candidate"
                            />
                        </>
                    )
                }



                {/* Sorting Options as Checkboxes */}
                {statusOptions.length > 0 && (
                    <>
                        <Typography variant="subtitle1" sx={{ mt: 1.5 }}>Filter by Status</Typography>
                        {statusOptions.map((option) => (
                            <FormControlLabel
                                sx={{ display: 'flex' }}
                                key={option.value}
                                control={
                                    <Checkbox
                                        checked={selectedStatuses.includes(option.value)}
                                        onChange={() => handleStatusChange(option.value)}
                                    />
                                }
                                label={option.label}
                            />
                        ))}
                    </>
                )}
            </DialogContent>
            <Divider />
            <DialogActions>
                <Box sx={{ display: 'flex', gap: 2, width: '100%', p: '0px 16px' }}>
                    <Button onClick={onClose} variant="outlined" fullWidth>
                        Cancel
                    </Button>
                    <Button onClick={handleApplyFilters} variant="contained" fullWidth>
                        Apply
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default FilterPreboardingCandidates;
